import React, { useRef } from 'react';
import { Image } from '../elements/Image';
import { Box, Stack, styled } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { updateRoomPhoto } from 'services/locations';
import { useFlash } from 'contexts/flash';

type Photo = {
  url: string;
  type: string;
};

interface IProps {
  outside: Photo;
  internal: Photo;
  submitId: number;
}

const RoomPhoto = ({ outside, internal, submitId }: IProps) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [internalPhoto, setInternalPhoto] = React.useState<File | null>(null);
  const [outsidePhoto, setOutsidePhoto] = React.useState<File | null>(null);
  const loading = useRef(false);
  const { setFlash } = useFlash();

  const onChangePhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && e.target.getAttribute('data-type') === 'internal') {
      setInternalPhoto(file);
    }
    if (file && e.target.getAttribute('data-type') === 'outside') {
      setOutsidePhoto(file);
    }
    e.target.value = '';
  };

  const onSaveInternalPhoto = async () => {
    if (!internalPhoto || loading.current) return;
    loading.current = true;
    try {
      const res = await updateRoomPhoto({ internalPhoto: internalPhoto, submitId });
      const newInternalPhoto = res.data?.submittedLocation?.submittedPhotos?.find(
        (photo: Photo) => photo.type === 'INTERNAL'
      );
      internal.url = newInternalPhoto?.url || internal.url;
      setInternalPhoto(null);
      setFlash({ type: 'success', message: 'Update Internal photo successful!' });
    } catch (error) {
      setFlash({ type: 'error', message: 'Update Internal photo failed!' });
    } finally {
      loading.current = false;
    }
  };

  const onSaveOutsidePhoto = async () => {
    if (!outsidePhoto || loading.current) return;
    loading.current = true;
    try {
      const res = await updateRoomPhoto({ outsidePhoto: outsidePhoto, submitId });
      const newOutsidePhoto = res.data?.submittedLocation?.submittedPhotos?.find(
        (photo: Photo) => photo.type === 'OUTSIDE'
      );
      outside.url = newOutsidePhoto?.url || outside.url;
      setOutsidePhoto(null);
      setFlash({ type: 'success', message: 'Update Outside photo successful!' });
    } catch (error) {
      setFlash({ type: 'error', message: 'Update Outside photo failed!' });
    } finally {
      loading.current = false;
    }
  };
  return (
    <Stack flexDirection={'row'} gap={2.5}>
      <WrapperImage>
        <Image
          url={{ ...outside, url: outsidePhoto ? URL.createObjectURL(outsidePhoto) : outside.url }}
          title={'Outside Photo'}
          styledImage={{ maxWidth: '300px', aspectRatio: '1', objectFit: 'cover' }}
        />
        {outsidePhoto ? (
          <BtnSave onClick={onSaveOutsidePhoto} />
        ) : (
          <BtnEdit
            onClick={() => {
              inputRef.current?.setAttribute('data-type', 'outside');
              inputRef.current?.click();
            }}
          />
        )}
      </WrapperImage>
      <WrapperImage>
        <Image
          panoramic
          url={{
            ...internal,
            url: internalPhoto ? URL.createObjectURL(internalPhoto) : internal.url
          }}
          title={'Internal Panoramic Photo'}
          styledImage={{ maxWidth: '300px', aspectRatio: '1', objectFit: 'cover' }}
        />

        {internalPhoto ? (
          <BtnSave onClick={onSaveInternalPhoto} style={{ right: 38 }} />
        ) : (
          <BtnEdit
            onClick={() => {
              inputRef.current?.setAttribute('data-type', 'internal');
              inputRef.current?.click();
            }}
            style={{ right: 38 }}
          />
        )}
      </WrapperImage>
      <input type="file" style={{ display: 'none' }} ref={inputRef} onChange={onChangePhoto} />
    </Stack>
  );
};

export default RoomPhoto;

const BtnEdit = styled(EditIcon)({
  position: 'absolute',
  top: 35,
  right: 0,
  color: 'white',
  zIndex: '9999',
  cursor: 'pointer',
  display: 'none',
  background: 'linear-gradient(rgba(50, 50, 93, 0.25), rgba(0, 0, 0, 0.3))',
  boxShadow: 'rgba(50, 50, 93, 0.25) 0px 13px 27px -5px, rgba(0, 0, 0, 0.3) 0px 8px 16px -8px'
});
const BtnSave = styled(SaveIcon)({
  position: 'absolute',
  top: 35,
  right: 0,
  color: 'white',
  zIndex: '99',
  cursor: 'pointer',
  background: 'linear-gradient(rgba(50, 50, 93, 0.25), rgba(0, 0, 0, 0.3))',
  boxShadow: 'rgba(50, 50, 93, 0.25) 0px 13px 27px -5px, rgba(0, 0, 0, 0.3) 0px 8px 16px -8px'
});
const WrapperImage = styled(Box)({
  position: 'relative',
  width: 'fit-content',
  borderRadius: 10,
  overflow: 'hidden',
  '&:hover svg': {
    display: 'block'
  }
});
